import { useCallback } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { getAdminGetProductTrialsQueryKey } from '@/api/auth/admin/admin';
import { useAdminCreateProductTrial, getAdminGetProductsQueryKey } from '@/api/auth/admin/admin';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EntitlementRuleFields } from '@/components/admin/EntitlementRuleFields';
import { toast } from 'sonner';
import { createProductTrialSchema, type CreateProductTrial } from '@/schemas/product-trial';
import { logger } from '@/services/logger';

/**
 * Props for the CreateTrialDialog component
 */
interface CreateTrialDialogProps {
  /** The product ID */
  productId: string;
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog open state changes */
  onOpenChange: (open: boolean) => void;
}

/**
 * Dialog component for creating a new product trial
 */
export function CreateTrialDialog({ productId, open, onOpenChange }: CreateTrialDialogProps) {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateProductTrial>({
    // The schema validates the generated polymorphic rule; cast bridges the resolver's input type.
    resolver: zodResolver(createProductTrialSchema) as Resolver<CreateProductTrial>,
    defaultValues: {
      rule: { type: 'cumulative', count: 1 },
      durationDays: undefined,
    },
  });

  const { mutate: createTrial } = useAdminCreateProductTrial({
    mutation: {
      onSuccess: () => {
        toast.success('Product trial created successfully');

        // Invalidate queries to refresh the lists
        queryClient.invalidateQueries({
          queryKey: getAdminGetProductTrialsQueryKey(productId),
        });
        queryClient.invalidateQueries({
          queryKey: getAdminGetProductsQueryKey(),
        });

        // Reset form and close dialog
        reset();
        onOpenChange(false);
      },
      onError: (error: any) => {
        logger.error(error, {
          context: 'CreateProductTrial',
          productId,
        });
        toast.error(
          'Failed to create product trial. ' + (error.response?.data?.detail || error.message)
        );
      },
    },
  });

  const onSubmit = useCallback(
    (data: CreateProductTrial) => {
      // Transform null to undefined for API compatibility
      const transformedData = {
        ...data,
        durationDays: data.durationDays ?? undefined,
      };

      createTrial({
        productId,
        data: transformedData,
      });
    },
    [createTrial, productId]
  );

  /**
   * Handle dialog close
   */
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && !isSubmitting) {
        reset();
      }
      onOpenChange(newOpen);
    },
    [isSubmitting, onOpenChange, reset]
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Product Trial</DialogTitle>
          <DialogDescription>
            Create a new trial token for this product. The token can be redeemed once by a user.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <EntitlementRuleFields
            setValue={setValue}
            watch={watch}
            fieldPath="rule"
            error={errors.rule?.message}
          />

          <div className="grid gap-2">
            <Label htmlFor="durationDays">Duration in Days (Optional)</Label>
            <Input
              id="durationDays"
              type="number"
              min="1"
              placeholder="30, 90, 365, or leave empty for perpetual"
              {...register('durationDays', {
                setValueAs: (value) => (value === '' || isNaN(value) ? undefined : Number(value)),
              })}
            />
            {errors.durationDays && (
              <p className="text-sm text-destructive">{errors.durationDays.message}</p>
            )}
            <p className="text-sm text-muted-foreground">
              Number of days for the trial. Examples: 30 (30 days), 365 (1 year). Leave empty for
              perpetual trial.
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Trial'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
